// ---------------------------------------------------------------------------
// Component Renderer Registry
// ---------------------------------------------------------------------------

import {
  SHARED_BLOCK_REGISTRY,
  normalizeBlockPropsForRender,
} from "../block-registry.js";
import type { ComponentRenderer } from "./types.js";

const REGISTRY = new Map<string, ComponentRenderer>();

export function registerRenderer(
  type: string,
  renderer: ComponentRenderer,
): void {
  REGISTRY.set(type, {
    render(props, context) {
      return renderer.render(
        normalizeBlockPropsForRender(type, props),
        context,
      );
    },
  });
}

// Editor keys (e.g. "BeforeAfter") differ from canonical renderer types
// (e.g. "before-after") for renderer-backed blocks. Puck records the editor
// key as `component.type` when saving, so we resolve aliases at render time.
// This is built lazily on first read because the renderer registry is
// populated by side-effect imports that may run before SHARED_BLOCK_REGISTRY
// finishes loading on some bundlers.
let editorKeyAliases: Map<string, string> | null = null;

function resolveCanonicalType(type: string): string {
  if (REGISTRY.has(type)) {
    return type;
  }
  if (editorKeyAliases === null) {
    editorKeyAliases = new Map();
    for (const block of SHARED_BLOCK_REGISTRY) {
      if (block.editorKey && block.editorKey !== block.type) {
        editorKeyAliases.set(block.editorKey, block.type);
      }
    }
  }
  return editorKeyAliases.get(type) ?? type;
}

export function getRenderer(type: string): ComponentRenderer | undefined {
  return REGISTRY.get(resolveCanonicalType(type));
}

export function getRegisteredTypes(): string[] {
  return [...REGISTRY.keys()];
}
